import { formatPercent } from 'components/Widget/shared/percent';
import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type { RateType, RateTypeOption, TermUnit, TermUnitOption } from './types';

export const TITLE = 'Loan Calculator';

export const MONTHS_PER_YEAR = 12;

export const RATE_TYPE_HINTS: Readonly<Record<RateType, string>> = {
  interestRate: 'The percentage a lender charges every year to borrow the money, before fees.',
  apr: "APR stands for Annual Percentage Rate. It's the total yearly cost of your loan, including interest plus fees, shown as one percentage annually.",
};

export const RATE_TYPE_OPTIONS: readonly RateTypeOption[] = [
  { key: 'interestRate', label: 'Interest Rate', hint: RATE_TYPE_HINTS.interestRate },
  { key: 'apr', label: 'APR', hint: RATE_TYPE_HINTS.apr },
];

export const DEFAULT_RATE_TYPE: RateTypeOption['key'] = 'interestRate';

export const TERM_UNIT_OPTIONS: readonly TermUnitOption[] = [
  { key: 'months', label: 'Months', monthsPerUnit: 1 },
  { key: 'years', label: 'Years', monthsPerUnit: MONTHS_PER_YEAR },
];

export const DEFAULT_TERM_UNIT: TermUnitOption['key'] = 'months';

// A closed set with a default, so a key that misses is a coding error rather than user input.
export const getTermUnit = (key: TermUnit): TermUnitOption => {
  const option = TERM_UNIT_OPTIONS.find(unit => unit.key === key);

  if (!option) throw new Error(`Unsupported term unit: ${key}`);

  return option;
};

/** Final copy from Legal, received 2026-09-18. */
export const DISCLAIMER =
  'This calculator is for educational purposes only and is not intended to offer any tax, legal, financial, or investment advice.';

/** The toggle reads "Extra payment (optional)"; the revealed field states the cadence it is paid on. */
export const EXTRA_PAYMENT_INPUT_LABEL = 'Additional monthly payment';

export const EXTRA_PAYMENT_SUFFIX = '/ month';

export const EXTRA_PAYMENT_FIELD: CalculatorField = {
  label: 'Extra payment',
  hint: 'An additional amount you pay toward your loan balance each month that can help you pay off the loan faster and with less interest.',
};

/** Design's greyed defaults; `valueOrPlaceholder` makes Calculate fall back to these same numbers. */
export const LOAN_AMOUNT_FIELD: CalculatorField = { label: 'Loan Amount', placeholder: '200' };

export const RATE_TYPE_LEGEND = 'Rate type';

export const TERM_UNIT_LEGEND = 'Loan term unit';

export const LOAN_TERM_FIELD: CalculatorField = { label: 'Loan Term', placeholder: '5' };

export const DEFAULT_RATE = 3;

/** The label is unused - the rate field takes its own from the rate-type radio - but the default is shared. */
export const RATE_FIELD: CalculatorField = { label: 'Rate', placeholder: formatPercent(DEFAULT_RATE) };

/** Matches the APY calculator's ceiling; a product-set bound is still open. */
export const MAX_FIELD_VALUE = 999999;

export const MAX_RATE = 100;

export const MAX_TERM_MONTHS = 600;

export const MAX_TERM_YEARS = 50;

/**
 * A one-month loan is a single closing payment, so an extra payment provably cannot save anything
 * and the comparison renders two identical columns. Two months is the shortest term the comparison
 * can say something true about. In years the floor stays 1, since a one-year loan is twelve
 * payments and an ordinary product.
 */
export const TERM_BOUNDS: Readonly<Record<TermUnit, { minimum: number; maximum: number }>> = {
  months: { minimum: 2, maximum: MAX_TERM_MONTHS },
  years: { minimum: 1, maximum: MAX_TERM_YEARS },
};

export const LOAN_AMOUNT_ERROR = 'Enter an amount between 1 and 999,999';

export const RATE_ERROR = 'Enter a rate between 0% and 100%';

/** Both bounds depend on the unit radio, so unlike its siblings this message cannot be a fixed string. */
export const TERM_ERROR = (termUnit: TermUnit): string => {
  const { minimum, maximum } = TERM_BOUNDS[termUnit];

  return `Enter a term between ${minimum} and ${maximum} ${termUnit}`;
};

export const EXTRA_PAYMENT_ERROR = 'Enter an amount between 1 and 999,999';

export const FORM_LABEL = 'Loan calculator';

export const INITIAL_CURRENCY_VALUES: CalculatorFieldValues = {
  [LOAN_AMOUNT_FIELD.label]: '',
  [EXTRA_PAYMENT_FIELD.label]: '',
};

export const INITIAL_TERM_VALUES: CalculatorFieldValues = { [LOAN_TERM_FIELD.label]: '' };

export const INITIAL_RATE_VALUES: CalculatorFieldValues = { [RATE_FIELD.label]: '' };

/** The teaser models this share of the scheduled payment when the user has not entered an extra one. */
export const DEFAULT_EXTRA_PAYMENT_RATE = 0.05;

/**
 * A 50-year term is past anything the field accepts, so reaching this means the balance stopped
 * falling and the schedule would never terminate.
 */
export const MAX_MONTHS = 600;

/**
 * Rounded to one decimal rather than truncated: a 49-month payoff is 4.1 years, and flooring it to
 * 4 would understate the payoff beside a `monthsSaved` figure that counts every month.
 */
export const formatYears = (months: number): string => {
  const years = Math.round((months / MONTHS_PER_YEAR) * 10) / 10;

  return `${years} ${years === 1 ? 'year' : 'years'}`;
};

/**
 * A year or less reads in months: the teaser's default extra clears only a handful of them, and
 * `0.3 years` sooner is a figure no borrower would act on.
 */
export const formatDuration = (months: number): string => {
  if (months <= MONTHS_PER_YEAR) return `${months} ${months === 1 ? 'month' : 'months'}`;

  return formatYears(months);
};

export const CURRENT_PLAN_HEADING = 'Current Plan';

export const WITH_EXTRA_PAYMENT_HEADING = 'With Extra Payment';

/**
 * The three figures the logic doc names, in its order. `years` reads `monthsToPayoff` because the
 * schedule is settled in months, and `formatDuration` picks the unit the value is legible in.
 */
export const PILL_ROWS = [
  { key: 'totalInterest', label: 'Est. total interest paid' },
  { key: 'years', label: 'Time to pay off' },
  { key: 'totalCost', label: 'Est. total cost of loan' },
] as const;

export const RESULT_COPY = {
  heroEyebrow: 'Estimated monthly payment',
  /** Suffixed onto the switch label so the figure being modelled is never a mystery. */
  comparisonToggleAmount: (amount: string) => `${amount}/mo`,
  /**
   * A short term saves interest without clearing a whole month, and "0 months sooner" reads as a
   * reason not to bother. The payoff clause is dropped rather than rounded, so the sentence only
   * promises what the schedule actually delivers.
   */
  teaser: (extra: string, interestSaved: string, monthsSaved: number) =>
    monthsSaved > 0
      ? `Paying an extra ${extra}/mo could save you an est. ${interestSaved} in interest and pay off your loan ${formatDuration(monthsSaved)} sooner.`
      : `Paying an extra ${extra}/mo could save you an est. ${interestSaved} in interest.`,
  /**
   * Carries the payoff saving because the pills do not: they state the two durations side by side,
   * leaving the difference between them for the reader to work out. Dropped at zero for the same
   * reason as the teaser.
   */
  comparison: (extra: string, interestSaved: string, monthsSaved: number) =>
    monthsSaved > 0
      ? `By adding ${extra} to your monthly payment, you'll save an est. ${interestSaved} in interest and pay off your loan ${formatDuration(monthsSaved)} sooner.`
      : `By adding ${extra} to your monthly payment, you'll save an est. ${interestSaved} in interest.`,
  ctaHeading: 'Fee-free banking with Chime.',
  ctaBody: 'Start banking with Chime today.',
  ctaLabel: 'Get started',
} as const;

/** Self-contained copy for the live-region announcement; the panel itself is too long to hear out. */
export const SUMMARY_COPY = {
  payment: (monthlyPayment: string, payoff: string) =>
    `Your estimated monthly payment is ${monthlyPayment}, paying the loan off in ${payoff}.`,
} as const;
